/**
 * Multileader (MLEADER) entity rendering — leader lines, dogleg,
 * content (MText / Block), annotative contexts, spline interpolation.
 *
 * Extracted from PrimitiveAtlasStage.tsx — pure structural refactor.
 */

import * as React from "react";

import type { EntityPreview } from "@dxf-ai-editor/contracts";
import type { BlockDefinition } from "@dxf-ai-editor/drawing-model";
import {
  readNumber,
  readPositiveNumber,
  type StagePoint,
} from "./renderHelpers";
import { arrowMarkerIdFromBlockName } from "./ArrowMarkerDefs";
import { renderBlockChildEntity } from "./blockRender";
import { catmullRomCubicSpans } from "../scene3d/adapters/geom/catmullRom";
import { splitLeaderLineAtBreaks } from "../scene3d/adapters/geom/leaderBreaks";

/* ── MULTILEADER renderer helpers (Phase 3, Priority #1) ─── */

// Compact JSON shapes used by enrichMultileaderEntities. Keep in sync
// with `encodeLeaderRoots` in dxfGeometry.ts.
export type EncodedLeaderLine = {
  v: [number, number][];
  b: [number, number, number, number][];
  a: string;
};
export type EncodedLeaderNode = {
  d: [number, number] | null;
  l: EncodedLeaderLine[];
};
export type EncodedLeaderRoot = { n: EncodedLeaderNode[] };

export type EncodedBlockAttrRef = {
  attDefHandle: string;
  index: number;
  width: number;
  value: string;
};

export type EncodedAnnotativeContext = {
  scale: number;
  textHeight: number;
  arrowheadSize: number;
  doglegLength: number;
  landingGap: number;
  textRotation: number;
  textColor: string;
  textWidth: number;
};

// AutoCAD MLEADERSTYLE property override flags — must mirror the
// MLEADER_OVERRIDE_FLAGS bitmask in apps/web/src/app/state/dxfGeometry.ts.
const MLEADER_OVERRIDE_LEADER_TYPE = 1 << 0;
const MLEADER_OVERRIDE_ENABLE_LANDING = 1 << 4;
const MLEADER_OVERRIDE_LANDING_GAP = 1 << 5;
const MLEADER_OVERRIDE_ENABLE_DOGLEG = 1 << 6;
const MLEADER_OVERRIDE_DOGLEG_LENGTH = 1 << 7;
const MLEADER_OVERRIDE_ARROWHEAD = 1 << 8;
const MLEADER_OVERRIDE_ARROWHEAD_SIZE = 1 << 9;
const MLEADER_OVERRIDE_CONTENT_TYPE = 1 << 10;
const MLEADER_OVERRIDE_TEXT_COLOR = 1 << 15;
const MLEADER_OVERRIDE_TEXT_HEIGHT = 1 << 16;
const MLEADER_OVERRIDE_TEXT_FRAME = 1 << 17;
const MLEADER_OVERRIDE_BLOCK_CONTENT_BLOCK = 1 << 19;
const MLEADER_OVERRIDE_BLOCK_CONTENT_COLOR = 1 << 20;
const MLEADER_OVERRIDE_BLOCK_CONTENT_SCALE = 1 << 21;
const MLEADER_OVERRIDE_BLOCK_CONTENT_ROTATION = 1 << 22;
const MLEADER_OVERRIDE_TEXT_ATTACHMENT_DIRECTION = 1 << 25;

// Convert a Catmull-Rom-style polyline (the user-clicked vertices) into
// an SVG cubic-Bezier path. The per-span tangent / control-point math is
// shared with the WebGL leader tessellation via `catmullRomCubicSpans`
// (`scene3d/adapters/geom/catmullRom.ts`) so both renderers draw the same
// curve; this builder just maps each span to an SVG `C` segment.
//
// We deliberately use cubic (`C` segments) — quadratic (`Q`) is
// noticeably worse on long winding leaders and is forbidden by
// CLAUDE.md Priority #1.
export function catmullRomToCubicBezierPath(vertices: StagePoint[]): string {
  const n = vertices.length;
  if (n === 0) return "";
  const v0 = vertices[0]!;
  if (n === 1) return `M ${v0.x} ${v0.y}`;
  if (n === 2) {
    const v1 = vertices[1]!;
    return `M ${v0.x} ${v0.y} L ${v1.x} ${v1.y}`;
  }

  const parts: string[] = [`M ${v0.x} ${v0.y}`];
  for (const span of catmullRomCubicSpans(vertices)) {
    parts.push(
      `C ${span.c1.x} ${span.c1.y} ${span.c2.x} ${span.c2.y} ${span.p2.x} ${span.p2.y}`,
    );
  }
  return parts.join(" ");
}

/** Read either an entity-level override or fall back to the style. */
function readMlNumberWithOverride(
  props: Record<string, string>,
  flagSet: boolean,
  entityKey: string,
  styleKey: string,
  fallback: number,
): number {
  const source = flagSet ? entityKey : styleKey;
  const sourceVal = Number(props[source]);
  if (Number.isFinite(sourceVal)) return sourceVal;
  // Fall back to the other source if the chosen one is missing.
  const altVal = Number(props[flagSet ? styleKey : entityKey]);
  return Number.isFinite(altVal) ? altVal : fallback;
}

function readMlStringWithOverride(
  props: Record<string, string>,
  flagSet: boolean,
  entityKey: string,
  styleKey: string,
  fallback: string,
): string {
  const sourceVal = props[flagSet ? entityKey : styleKey];
  if (sourceVal && sourceVal.length > 0) return sourceVal;
  const altVal = props[flagSet ? styleKey : entityKey];
  return altVal && altVal.length > 0 ? altVal : fallback;
}

function readMlBoolWithOverride(
  props: Record<string, string>,
  flagSet: boolean,
  entityKey: string,
  styleKey: string,
  fallback: boolean,
): boolean {
  const sourceVal = props[flagSet ? entityKey : styleKey];
  if (sourceVal === "true") return true;
  if (sourceVal === "false") return false;
  const altVal = props[flagSet ? styleKey : entityKey];
  if (altVal === "true") return true;
  if (altVal === "false") return false;
  return fallback;
}

export function pickAnnotativeContext(
  contexts: EncodedAnnotativeContext[],
  annotationScale: number | null,
): EncodedAnnotativeContext | null {
  if (contexts.length === 0 || annotationScale == null) return null;
  const epsilon = 1e-6;
  return (
    contexts.find((c) => Math.abs(c.scale - annotationScale) < epsilon) ??
    contexts.find((c) => Math.abs(c.scale - 1) < epsilon) ??
    contexts[0] ??
    null
  );
}

// Recursive renderer for block content has been moved to
// apps/web/src/app/scene/blockRender.tsx so it can be reused by both
// MLEADER block content and the top-level INSERT renderer. This module
// imports it as `renderBlockChildEntity` (see imports at the top).
export function renderMultileader(
  entity: EntityPreview,
  blockDefinitions: Record<string, BlockDefinition>,
  annotationScale: number | null,
) {
  const props = entity.properties;
  // ── 1. Decode the leader hierarchy from the property blob. ──
  let leaderRoots: EncodedLeaderRoot[] = [];
  try {
    leaderRoots = JSON.parse(props.__mleader_leader_roots ?? "[]");
  } catch {
    leaderRoots = [];
  }
  let blockAttrRefs: EncodedBlockAttrRef[] = [];
  try {
    blockAttrRefs = JSON.parse(props.__mleader_block_attrs ?? "[]");
  } catch {
    blockAttrRefs = [];
  }
  let annotativeContexts: EncodedAnnotativeContext[] = [];
  try {
    annotativeContexts = JSON.parse(
      props.__mleader_annotative_contexts ?? "[]",
    );
  } catch {
    annotativeContexts = [];
  }

  // ── 2. Resolve the active style values via override flags + ────
  // ──    annotative context. The MLENT_* keys are the entity's   ──
  // ──    own values; MLVAR_* keys are the style table values.    ──
  const flags = Math.round(readNumber(props, "MLENT_PROPERTY_OVERRIDE_FLAGS"));

  // Annotative context (B2) — overrides everything else when present.
  // PR-14 — MLENT_ANNOTATIVE_ENABLED is derived in previewBuilders from
  // entity.annotativeContexts.length > 0 (no longer a stored boolean).
  const annotativeEnabled = props.MLENT_ANNOTATIVE_ENABLED === "true";
  const ctx = annotativeEnabled
    ? pickAnnotativeContext(annotativeContexts, annotationScale)
    : null;

  // Resolve every renderer-relevant value, in priority order:
  //   annotative context > entity override > style > hard-coded default.
  const leaderType = ctx
    ? Math.round(readNumber(props, "MLENT_LEADER_TYPE", 1))
    : Math.round(
        readMlNumberWithOverride(
          props,
          (flags & MLEADER_OVERRIDE_LEADER_TYPE) !== 0,
          "MLENT_LEADER_TYPE",
          "MLVAR_LEADER_TYPE",
          1,
        ),
      );

  const arrowheadBlock = readMlStringWithOverride(
    props,
    (flags & MLEADER_OVERRIDE_ARROWHEAD) !== 0,
    "MLENT_ARROWHEAD_BLOCK",
    "MLVAR_ARROWHEAD_BLOCK",
    "",
  );
  const arrowheadSize =
    ctx?.arrowheadSize ??
    readMlNumberWithOverride(
      props,
      (flags & MLEADER_OVERRIDE_ARROWHEAD_SIZE) !== 0,
      "MLENT_ARROWHEAD_SIZE",
      "MLVAR_ARROWHEAD_SIZE",
      2.5,
    );

  const landingEnabled = readMlBoolWithOverride(
    props,
    (flags & MLEADER_OVERRIDE_ENABLE_LANDING) !== 0,
    "MLENT_LANDING_ENABLED",
    "MLVAR_LANDING_ENABLED",
    true,
  );
  const doglegEnabled = readMlBoolWithOverride(
    props,
    (flags & MLEADER_OVERRIDE_ENABLE_DOGLEG) !== 0,
    "MLENT_DOGLEG_ENABLED",
    "MLVAR_DOGLEG_ENABLED",
    true,
  );
  const doglegLength =
    ctx?.doglegLength ??
    readMlNumberWithOverride(
      props,
      (flags & MLEADER_OVERRIDE_DOGLEG_LENGTH) !== 0,
      "MLENT_DOGLEG_LENGTH",
      "MLVAR_DOGLEG_LENGTH",
      8,
    );
  const landingGap =
    ctx?.landingGap ??
    readMlNumberWithOverride(
      props,
      (flags & MLEADER_OVERRIDE_LANDING_GAP) !== 0,
      "MLENT_LANDING_GAP",
      "MLVAR_LANDING_GAP",
      2,
    );

  const contentType = Math.round(
    readMlNumberWithOverride(
      props,
      (flags & MLEADER_OVERRIDE_CONTENT_TYPE) !== 0,
      "MLENT_CONTENT_TYPE",
      "MLVAR_CONTENT_TYPE",
      2,
    ),
  );
  const textHeight =
    ctx?.textHeight ??
    readMlNumberWithOverride(
      props,
      (flags & MLEADER_OVERRIDE_TEXT_HEIGHT) !== 0,
      "MLENT_TEXT_HEIGHT",
      "MLVAR_TEXT_HEIGHT",
      2.5,
    );
  const textRotation =
    ctx?.textRotation ?? readNumber(props, "MLENT_TEXT_ROTATION", 0);
  const textColor =
    ctx?.textColor ??
    readMlStringWithOverride(
      props,
      (flags & MLEADER_OVERRIDE_TEXT_COLOR) !== 0,
      "MLENT_TEXT_COLOR",
      "MLVAR_TEXT_COLOR",
      "currentColor",
    );
  const textWidth = ctx?.textWidth ?? readNumber(props, "MLENT_TEXT_WIDTH", 0);
  const textFrameDisplay = readMlBoolWithOverride(
    props,
    (flags & MLEADER_OVERRIDE_TEXT_FRAME) !== 0,
    "MLENT_TEXT_FRAME",
    "MLVAR_TEXT_FRAME",
    false,
  );
  const textAttachmentDirection = Math.round(
    readMlNumberWithOverride(
      props,
      (flags & MLEADER_OVERRIDE_TEXT_ATTACHMENT_DIRECTION) !== 0,
      "MLENT_TEXT_ATTACHMENT_DIRECTION",
      "MLVAR_TEXT_ATTACHMENT_DIRECTION",
      0,
    ),
  );
  const textLineSpacingFactor = readPositiveNumber(
    props,
    "MLENT_TEXT_LINE_SPACING_FACTOR",
    1,
  );

  // Block content
  const blockContentName = readMlStringWithOverride(
    props,
    (flags & MLEADER_OVERRIDE_BLOCK_CONTENT_BLOCK) !== 0,
    "MLENT_BLOCK_NAME",
    "MLVAR_BLOCK_NAME",
    "",
  );
  const blockScale = readMlNumberWithOverride(
    props,
    (flags & MLEADER_OVERRIDE_BLOCK_CONTENT_SCALE) !== 0,
    "MLENT_BLOCK_SCALE",
    "MLVAR_BLOCK_SCALE",
    1,
  );
  const blockRotation = readMlNumberWithOverride(
    props,
    (flags & MLEADER_OVERRIDE_BLOCK_CONTENT_ROTATION) !== 0,
    "MLENT_BLOCK_ROTATION",
    "MLVAR_BLOCK_ROTATION",
    0,
  );
  const blockColor = readMlStringWithOverride(
    props,
    (flags & MLEADER_OVERRIDE_BLOCK_CONTENT_COLOR) !== 0,
    "MLENT_BLOCK_COLOR",
    "MLVAR_BLOCK_COLOR",
    "currentColor",
  );

  const textLocationX = readNumber(props, "Landing X");
  const textLocationY = readNumber(props, "Landing Y");
  const contents = props.MLENT_TEXT_CONTENTS || props.Contents || entity.label;

  const arrowMarkerId = arrowMarkerIdFromBlockName(arrowheadBlock);

  // ── 3. Render every leader line in every node in every root. ──
  const segments: React.ReactNode[] = [];
  let segmentKey = 0;
  for (const root of leaderRoots) {
    if (!root) continue;
    for (const node of root.n) {
      if (!node) continue;
      const nodeLines = node.l;
      for (let li = 0; li < nodeLines.length; li++) {
        const line = nodeLines[li];
        if (!line || line.v.length === 0) continue;

        // Decode vertices into StagePoint shape so the helpers can use them.
        // When the leader line has only a single vertex (the arrow point,
        // which is the common case for entities created via the MLEADER
        // command), we synthesize a second vertex from the entity's landing
        // point (textLocation) so the polyline has at least 2 points and
        // actually renders. This matches AutoCAD's behaviour where the
        // landing endpoint is implicit — the arrow→landing segment is the
        // primary leader line.
        let verts: StagePoint[] = line.v.map(([vx, vy]) => ({
          x: vx,
          y: vy,
        }));
        if (verts.length === 1) {
          verts = [...verts, { x: textLocationX, y: textLocationY }];
        }
        const lineArrow = line.a || arrowheadBlock;
        const lineMarkerId = arrowMarkerIdFromBlockName(lineArrow);

        // Honour breakPoints: split the polyline at each break's gap
        // (start → end). The split rule (nearest-segment-midpoint, a
        // documented approximation of AutoCAD's exact arc-length offsets)
        // is shared with the WebGL leader walker via `splitLeaderLineAtBreaks`
        // so the two backends cut the stroke at the SAME place (Codex S9b P2).
        // The first surviving sub-run carries the arrowhead marker.
        type SubSegment = { verts: StagePoint[]; applyMarker: boolean };
        const subSegments: SubSegment[] = splitLeaderLineAtBreaks(
          verts,
          line.b.map(([sx, sy, ex, ey]) => ({
            start: { x: sx, y: sy },
            end: { x: ex, y: ey },
          })),
          (x, y) => ({ x, y }),
        ).map((subVerts, idx) => ({ verts: subVerts, applyMarker: idx === 0 }));

        for (const sub of subSegments) {
          if (sub.verts.length < 2) continue;
          // Spline (cubic Bezier) when leaderType=2; straight polyline
          // when leaderType=1; nothing when leaderType=0.
          if (leaderType === 0) {
            continue;
          }
          if (leaderType === 2) {
            const d = catmullRomToCubicBezierPath(sub.verts);
            segments.push(
              <path
                key={`mleader-line-${segmentKey++}`}
                d={d}
                fill="none"
                className="drawing-stage-entity-stroke"
                markerStart={
                  sub.applyMarker ? `url(#${lineMarkerId})` : undefined
                }
              />,
            );
          } else {
            const points = sub.verts.map((v) => `${v.x},${v.y}`).join(" ");
            segments.push(
              <polyline
                key={`mleader-line-${segmentKey++}`}
                points={points}
                fill="none"
                className="drawing-stage-entity-stroke"
                markerStart={
                  sub.applyMarker ? `url(#${lineMarkerId})` : undefined
                }
              />,
            );
          }
        }

        // ── Landing / dogleg ──
        // Append a landing-segment from the LAST vertex of the LAST
        // leader line in this node to the textLocation. We use the
        // node's doglegVector if present, otherwise a horizontal
        // segment of length `doglegLength` toward the text.
        const last = verts[verts.length - 1];
        if (
          landingEnabled &&
          doglegEnabled &&
          li === nodeLines.length - 1 &&
          last
        ) {
          let endX: number;
          let endY: number;
          if (node.d) {
            const dx = node.d[0];
            const dy = node.d[1];
            const mag = Math.hypot(dx, dy) || 1;
            endX = last.x + (dx / mag) * doglegLength;
            endY = last.y + (dy / mag) * doglegLength;
          } else {
            // Default: horizontal toward textLocation.
            const sign = textLocationX >= last.x ? 1 : -1;
            endX = last.x + sign * doglegLength;
            endY = last.y;
          }
          segments.push(
            <line
              key={`mleader-dogleg-${segmentKey++}`}
              x1={last.x}
              y1={last.y}
              x2={endX}
              y2={endY}
              className="drawing-stage-entity-stroke"
            />,
          );
        }
      }
    }
  }

  // ── 4. Content (text or block) ──
  let contentNode: React.ReactNode = null;
  if (contentType === 2) {
    // MText content. Multi-line via `\\P` and `\n`.
    const lines = contents.split(/\\P|\n/g);
    const lineHeight = textHeight * textLineSpacingFactor;
    const textTransform =
      textRotation !== 0
        ? `rotate(${-textRotation} ${textLocationX} ${textLocationY})`
        : undefined;

    // Compute a rough bounding box for the optional text frame so we
    // can outline it without measuring at runtime. Width = longest
    // visible line * fontHeight * 0.6 (typical glyph aspect), or
    // textWidth if explicitly set.
    const longestLineLen = lines.reduce(
      (max, line) => Math.max(max, line.length),
      0,
    );
    const estimatedWidth =
      textWidth > 0
        ? textWidth
        : Math.max(1, longestLineLen) * textHeight * 0.6;
    const estimatedHeight = Math.max(1, lines.length) * lineHeight;
    const frameOffset = Math.max(landingGap, textHeight * 0.2);

    contentNode = (
      <g>
        {textFrameDisplay && (
          <rect
            x={textLocationX - frameOffset}
            y={textLocationY - estimatedHeight + frameOffset}
            width={estimatedWidth + frameOffset * 2}
            height={estimatedHeight + frameOffset * 2}
            fill="none"
            className="drawing-stage-entity-stroke"
          />
        )}
        <text
          x={textLocationX}
          y={textLocationY}
          fontSize={textHeight}
          fill={
            textColor === "ByLayer" || textColor === "ByBlock"
              ? "currentColor"
              : textColor
          }
          transform={textTransform}
          textAnchor={textAttachmentDirection === 1 ? "middle" : "start"}
          className="drawing-stage-text-entity"
        >
          {lines.map((lineText, idx) => (
            <tspan
              key={`mleader-text-line-${idx}`}
              x={textLocationX}
              dy={idx === 0 ? 0 : lineHeight}
            >
              {lineText}
            </tspan>
          ))}
        </text>
      </g>
    );
  } else if (contentType === 1) {
    // Block content. Look up the block definition and render its
    // entities recursively. ATTDEF entries inside the block resolve
    // to the matching entry in `blockAttributeReferences[]`.
    const def = blockContentName ? blockDefinitions[blockContentName] : null;
    const attrLookup = new Map<string, string>();
    for (const ref of blockAttrRefs) {
      attrLookup.set(ref.attDefHandle, ref.value);
    }
    if (def) {
      const transform = `translate(${textLocationX} ${textLocationY}) scale(${blockScale}) rotate(${-blockRotation})`;
      contentNode = (
        <g
          transform={transform}
          stroke={
            blockColor === "ByLayer" || blockColor === "ByBlock"
              ? "currentColor"
              : blockColor
          }
        >
          {def.entities.map((child, idx) =>
            renderBlockChildEntity(
              child,
              attrLookup,
              blockDefinitions,
              10,
              `mleader-block-${entity.id}-${idx}`,
            ),
          )}
        </g>
      );
    } else if (blockContentName) {
      // Missing block definition — never silently drop, render a
      // labelled marker so the user knows the link is broken.
      contentNode = (
        <g>
          <circle
            cx={textLocationX}
            cy={textLocationY}
            r={textHeight}
            fill="none"
            className="drawing-stage-entity-stroke"
          />
          <text
            x={textLocationX + textHeight + 2}
            y={textLocationY}
            fontSize={textHeight}
            className="drawing-stage-text-entity"
          >
            {`?${blockContentName}`}
          </text>
        </g>
      );
    }
  }
  // contentType === 0 → no content rendered (still draws leader lines).

  // Suppress unused-variable lint warnings for fields the renderer
  // exposes for future grip-edit work but does not draw yet.
  void textWidth;
  void arrowheadSize;
  void arrowMarkerId;

  return (
    <g>
      {segments}
      {contentNode}
    </g>
  );
}
